import { isResource } from '@medplum/core'
import type { Coding, Patient, Reference, Schedule } from '@medplum/fhirtypes'
import { formatInTimeZone } from 'date-fns-tz'
import { compact } from 'lodash-es'
import { SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export const SCHEDULE_SUBTYPE_SYSTEM = `${SEEN_HEALTH_SYSTEM}/schedule-subtype`
export const DAY_CENTER_ATTENDANCE_SUBTYPE = 'day-center-attendance'
export const DAY_CENTER_ATTENDANCE_DAY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/day-center-attendance-day`
export const DAY_CENTER_PREFERRED_APPOINTMENT_DAY_SYSTEM = `${SEEN_HEALTH_SYSTEM}/day-center-preferred-appointment-day`
export const DAY_CENTER_ATTENDANCE_SHIFT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/day-center-attendance-shift`
export const DAY_CENTER_PREFERRED_APPOINTMENT_SHIFT_SYSTEM = `${SEEN_HEALTH_SYSTEM}/day-center-preferred-appointment-shift`

export const ATTENDANCE_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const
export type AttendanceWeekday = (typeof ATTENDANCE_WEEKDAYS)[number]

export const ATTENDANCE_SHIFTS = ['am', 'pm'] as const
export type AttendanceShift = (typeof ATTENDANCE_SHIFTS)[number]

interface AttendanceWeekdaysByIsoDay {
  [isoDay: number]: AttendanceWeekday | undefined
}

const ATTENDANCE_WEEKDAY_BY_ISO_DAY: AttendanceWeekdaysByIsoDay = {
  1: 'mon',
  2: 'tue',
  3: 'wed',
  4: 'thu',
  5: 'fri',
}

// The weekday an instant falls on in the clinic's zone. Undefined covers
// Saturday and Sunday, which attendance never names. The zone is the caller's
// to supply because the same instant is a different weekday in UTC.
export function attendanceWeekdayInZone(
  date: Date | string,
  timeZone: string
): AttendanceWeekday | undefined {
  return ATTENDANCE_WEEKDAY_BY_ISO_DAY[
    Number(formatInTimeZone(date, timeZone, 'i'))
  ]
}

// The shift worked on each day; days without a shift are absent.
export type AttendanceShiftsByDay = Partial<
  Record<AttendanceWeekday, AttendanceShift>
>

// How a set of per-day shifts reads as a single control: one shift when every
// day agrees, otherwise `custom`. Days with no shift at all read as
// `undefined` — nothing is selected.
export type ShiftMode = AttendanceShift | 'custom'

// Collapses per-day shifts over `days` into the single mode a shift control
// shows. Deriving this (rather than storing it) keeps the mode from ever
// contradicting the per-day shifts it summarizes.
export function shiftModeFor(
  shifts: AttendanceShiftsByDay,
  days: AttendanceWeekday[]
): ShiftMode | undefined {
  const assigned = compact(days.map((day) => shifts[day]))
  if (assigned.length === 0) {
    return undefined
  }
  // A day left unset among days that do have shifts is itself a custom split.
  if (assigned.length < days.length) {
    return 'custom'
  }
  return assigned.every((shift) => shift === assigned[0])
    ? assigned[0]
    : 'custom'
}

// `${day}-${shift}`, e.g. `mon-am`. One tag per day keeps each day's shift
// independently searchable, matching how the day tags themselves are stored.
function encodeShiftCode(
  day: AttendanceWeekday,
  shift: AttendanceShift
): string {
  return `${day}-${shift}`
}

function decodeShiftCode(
  code: string
): { day: AttendanceWeekday; shift: AttendanceShift } | undefined {
  const [day, shift] = code.split('-')
  if (!ATTENDANCE_WEEKDAYS.includes(day as AttendanceWeekday)) {
    return undefined
  }
  if (!ATTENDANCE_SHIFTS.includes(shift as AttendanceShift)) {
    return undefined
  }
  return { day: day as AttendanceWeekday, shift: shift as AttendanceShift }
}

// Reads intersect with `days` for the same reason `preferredAppointmentDays`
// does: a shift for a day that is not in the set is meaningless.
function readShifts(
  codings: Coding[] | undefined,
  days: AttendanceWeekday[]
): AttendanceShiftsByDay {
  const allowed = new Set(days)
  const shifts: AttendanceShiftsByDay = {}
  for (const code of compact(codings?.map((coding) => coding.code))) {
    const decoded = decodeShiftCode(code)
    if (decoded && allowed.has(decoded.day)) {
      shifts[decoded.day] = decoded.shift
    }
  }
  return shifts
}

function writeShifts(
  shifts: AttendanceShiftsByDay,
  days: AttendanceWeekday[],
  system: string
): Coding[] {
  return ATTENDANCE_WEEKDAYS.flatMap((day) => {
    if (!days.includes(day)) {
      return []
    }
    const shift = shifts[day]
    return shift ? [{ system, code: encodeShiftCode(day, shift) }] : []
  })
}

/**
 * A participant's day-center attendance, modeled as a FHIR `Schedule` whose
 * `actor` is the participant. The attended weekdays live in `meta.tag` (one tag
 * per day) so they can be searched server-side; the preferred appointment days
 * and the per-day shifts for both use the same shape under their own tag
 * systems; the free-text `note` maps to `Schedule.comment`.
 */
export class DayCenterSchedule extends ExtendedResource<Schedule> {
  static readonly RESOURCE_TYPE = 'Schedule'

  // Discriminate by the subtype tag so a future Schedule wrapper can coexist —
  // matches only day-center schedules, consistent with `queryByParticipant`.
  static isInstance(value: unknown): value is Schedule {
    return (
      isResource(value) &&
      value.resourceType === 'Schedule' &&
      (value.meta?.tag ?? []).some(
        (tag) =>
          tag.system === SCHEDULE_SUBTYPE_SYSTEM &&
          tag.code === DAY_CENTER_ATTENDANCE_SUBTYPE
      )
    )
  }

  @Tag({ system: SCHEDULE_SUBTYPE_SYSTEM })
  _subtypeTag?: Coding

  @Tag({ system: DAY_CENTER_ATTENDANCE_DAY_SYSTEM, allowMultiple: true })
  _attendanceDays?: Coding[]

  @Tag({
    system: DAY_CENTER_PREFERRED_APPOINTMENT_DAY_SYSTEM,
    allowMultiple: true,
  })
  _preferredAppointmentDays?: Coding[]

  @Tag({ system: DAY_CENTER_ATTENDANCE_SHIFT_SYSTEM, allowMultiple: true })
  _attendanceShifts?: Coding[]

  @Tag({
    system: DAY_CENTER_PREFERRED_APPOINTMENT_SHIFT_SYSTEM,
    allowMultiple: true,
  })
  _preferredAppointmentShifts?: Coding[]

  constructor({ participant }: { participant: Reference<Patient> }) {
    super({ actor: [participant], active: true })
    this._subtypeTag = {
      system: SCHEDULE_SUBTYPE_SYSTEM,
      code: DAY_CENTER_ATTENDANCE_SUBTYPE,
    }
  }

  get participant(): Reference<Patient> | undefined {
    return this.resource.actor?.find((actor) => isOfType(actor, 'Patient'))
  }

  get attendanceDays(): AttendanceWeekday[] {
    const codes = new Set(
      compact(this._attendanceDays?.map((coding) => coding.code))
    )
    return ATTENDANCE_WEEKDAYS.filter((day) => codes.has(day))
  }

  set attendanceDays(days: AttendanceWeekday[]) {
    const ordered = ATTENDANCE_WEEKDAYS.filter((day) => days.includes(day))
    this._attendanceDays = ordered.map((code) => ({
      system: DAY_CENTER_ATTENDANCE_DAY_SYSTEM,
      code,
    }))
    // Attendance shifts are one-per-attended-day, so prune them to the new set
    // (re-assigning re-writes only the surviving days). Preferred appointment
    // days are chosen independently and are left untouched.
    if (this._attendanceShifts?.length) {
      // oxlint-disable-next-line no-self-assign -- re-runs the setter to prune shifts
      this.attendanceShifts = this.attendanceShifts
    }
  }

  // Preferred external-appointment days are chosen independently of the
  // day-center attendance days — a participant can prefer any weekday, whether
  // or not they attend the center that day.
  get preferredAppointmentDays(): AttendanceWeekday[] {
    const codes = new Set(
      compact(this._preferredAppointmentDays?.map((coding) => coding.code))
    )
    return ATTENDANCE_WEEKDAYS.filter((day) => codes.has(day))
  }

  set preferredAppointmentDays(days: AttendanceWeekday[]) {
    const ordered = ATTENDANCE_WEEKDAYS.filter((day) => days.includes(day))
    this._preferredAppointmentDays = ordered.map((code) => ({
      system: DAY_CENTER_PREFERRED_APPOINTMENT_DAY_SYSTEM,
      code,
    }))
    // Preferred shifts are one-per-preferred-day, so prune them to the new set.
    if (this._preferredAppointmentShifts?.length) {
      // oxlint-disable-next-line no-self-assign -- re-runs the setter to prune shifts
      this.preferredAppointmentShifts = this.preferredAppointmentShifts
    }
  }

  // The AM/PM shift attended on each day center day.
  get attendanceShifts(): AttendanceShiftsByDay {
    return readShifts(this._attendanceShifts, this.attendanceDays)
  }

  set attendanceShifts(shifts: AttendanceShiftsByDay) {
    this._attendanceShifts = writeShifts(
      shifts,
      this.attendanceDays,
      DAY_CENTER_ATTENDANCE_SHIFT_SYSTEM
    )
  }

  // The AM/PM shift preferred on each preferred appointment day.
  get preferredAppointmentShifts(): AttendanceShiftsByDay {
    return readShifts(
      this._preferredAppointmentShifts,
      this.preferredAppointmentDays
    )
  }

  set preferredAppointmentShifts(shifts: AttendanceShiftsByDay) {
    this._preferredAppointmentShifts = writeShifts(
      shifts,
      this.preferredAppointmentDays,
      DAY_CENTER_PREFERRED_APPOINTMENT_SHIFT_SYSTEM
    )
  }

  // The attended day and its shift for the day an instant falls on, or
  // undefined when the participant does not attend that day.
  attendanceOn(
    date: Date | string,
    timeZone: string
  ):
    | { day: AttendanceWeekday; shift: AttendanceShift | undefined }
    | undefined {
    const day = attendanceWeekdayInZone(date, timeZone)
    if (!day || !this.attendanceDays.includes(day)) {
      return undefined
    }
    return { day, shift: this.attendanceShifts[day] }
  }

  get note(): string | undefined {
    return this.resource.comment
  }

  set note(value: string | undefined) {
    this.resource.comment = value?.trim() || undefined
  }

  /** All day-center attendance schedules. */
  static baseSearchQuery(): QueryTypes {
    return {
      _tag: `${SCHEDULE_SUBTYPE_SYSTEM}|${DAY_CENTER_ATTENDANCE_SUBTYPE}`,
      _count: '1000',
    }
  }

  static queryByParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      actor: participant,
      _tag: `${SCHEDULE_SUBTYPE_SYSTEM}|${DAY_CENTER_ATTENDANCE_SUBTYPE}`,
      _count: '1',
    }
  }

  // One search for many participants — `normalizeQuery` comma-joins the array
  // into a single `actor` value (FHIR reference-OR).
  static byParticipants(participants: Reference<Patient>[]): QueryTypes {
    return {
      actor: participants,
      _tag: `${SCHEDULE_SUBTYPE_SYSTEM}|${DAY_CENTER_ATTENDANCE_SUBTYPE}`,
      _count: '1000',
    }
  }

  /**
   * Day-center schedules that include any of the given weekdays. Multiple days
   * are OR'd — `normalizeQuery` comma-joins the array into one `_tag` value
   * (FHIR token-OR). The day-tag system is unique to day-center schedules, so
   * the day tags alone are sufficiently discriminating.
   */
  static byAttendanceDays(days: AttendanceWeekday[]): QueryTypes {
    const dayTags = days.map(
      (day) => `${DAY_CENTER_ATTENDANCE_DAY_SYSTEM}|${day}`
    )
    return {
      // No days OR's to an empty `_tag`, which would match every Schedule —
      // fall back to a sentinel that matches nothing so the query stays bounded.
      _tag:
        dayTags.length > 0
          ? dayTags
          : `${DAY_CENTER_ATTENDANCE_DAY_SYSTEM}|__none__`,
      _count: '1000',
    }
  }
}
